import { supabase } from './supabaseClient';

// A value counts as "entered" when it is not blank/undefined/null. 0 IS a real
// entered value (e.g. 0 deposits) and must be saved; '' / null / undefined are
// "left blank" and produce no row — mirrors the KPI bundle's blank-skip rule.
const isEntered = (v) => v !== '' && v !== null && v !== undefined;

export const performanceService = {
  // The user's active PI-system assignments (RLS returns own rows). Mirrors
  // kpiService.getAssignedSystems (both return all of a user's active systems).
  async getAssignedPISystems(userId) {
    const { data, error } = await supabase
      .from('user_pi_system_assignments')
      .select('*, pi_systems(*)')
      .eq('user_id', userId)
      .eq('is_active', true);
    if (error) throw error;
    return data;
  },

  // Existing performance_data line-items for one submission (mirror getSubmissionKpis).
  async getSubmissionPerformance(submissionId) {
    const { data, error } = await supabase
      .from('performance_data')
      .select('*')
      .eq('submission_id', submissionId);
    if (error) throw error;
    return data;
  },

  // Upsert one performance_data row per non-blank item, keyed on
  // (submission_id, pi_parameter_id) — targets the NON-PARTIAL unique index
  // uq_perf_submission_parameter so PostgREST onConflict resolves it.
  async saveSubmissionPerformance(submissionId, userId, items) {
    const rows = items
      .filter((it) => isEntered(it.value))
      .map((it) => ({
        submission_id: submissionId,
        user_id: userId,
        pi_parameter_id: it.pi_parameter_id,
        value: it.value,
      }));
    if (rows.length === 0) return [];
    const { data, error } = await supabase
      .from('performance_data')
      .upsert(rows, { onConflict: 'submission_id,pi_parameter_id' })
      .select();
    if (error) throw error;
    return data;
  },

  // All expense line-items for a submission.
  async getExpenseItems(submissionId) {
    const { data, error } = await supabase
      .from('pi_expense_items')
      .select('*')
      .eq('submission_id', submissionId);
    if (error) throw error;
    return data;
  },

  // Replace-set: delete the submission's existing items, then insert the current
  // set (rows have no stable business key, so replace beats upsert). `dbRows` are
  // already shaped { item, link, amount }.
  async saveExpenseItems(submissionId, userId, dbRows) {
    const { error: delErr } = await supabase
      .from('pi_expense_items')
      .delete()
      .eq('submission_id', submissionId);
    if (delErr) throw delErr;
    if (!dbRows || dbRows.length === 0) return [];
    const rows = dbRows.map((r, i) => ({ ...r, submission_id: submissionId, user_id: userId, position: i }));
    const { data, error } = await supabase.from('pi_expense_items').insert(rows).select();
    if (error) throw error;
    return data;
  },
};
